// Lightweight typewriter reveal for headings + prose lines.
// Respects `prefers-reduced-motion` (returns text immediately).
import { useEffect, useRef, useState } from 'react';

const REDUCED = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function useTypewriter(text, { speed = 45, startDelay = 0 } = {}) {
  const [out, setOut] = useState(REDUCED() ? text : '');
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (REDUCED()) {
      setOut(text);
      return () => {};
    }
    setOut('');
    let cancelled = false;
    let i = 0;

    const tick = () => {
      if (cancelled) return;
      i += 1;
      setOut(text.slice(0, i));
      if (i < text.length) {
        timerRef.current = setTimeout(tick, speed);
      }
    };

    const kickoff = setTimeout(() => {
      if (!cancelled) tick();
    }, startDelay);

    return () => {
      cancelled = true;
      clearTimeout(kickoff);
      clearTimeout(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [text, speed, startDelay]);

  return out;
}

export function TypewriterText({
  text,
  speed = 45,
  startDelay = 0,
  cursor = true,
  className = '',
}) {
  const typed = useTypewriter(text, { speed, startDelay });
  const done = typed.length === text.length;
  return (
    <span className={className} data-typewriter data-typewriter-done={done ? '1' : '0'}>
      {typed}
      {cursor && (
        <span className="crt-cursor" aria-hidden="true">
          _
        </span>
      )}
    </span>
  );
}
